/**
 * หน้ารายชื่อผู้ร่วมพิธี `/names/?o=<CO…>&k=<รหัสลับ>` (2 พ.ย. 2569) — แทนเว็บเดิม sri-ceremony.netlify.app
 * ข้อมูลจาก memberApi `ceremonyNames` (repo Admin `src/member/ceremonyNames.js`) · ค่าตั้งต่อพิธี Firestore `ceremonyNamesPages/{CeremonyID}`
 * ชื่อธีมต้องตรงกับ CF THEMES / แอดมิน CEREMONY_NAMES_THEMES
 */

export type NamesTheme = 'auto' | 'classic' | 'glass' | 'smoky' | 'ground'
export const NAMES_THEMES: NamesTheme[] = ['auto', 'classic', 'glass', 'smoky', 'ground']

export type NameRow = { no: number; name: string; birthday: string; mine: boolean }
export type NamesResponse = {
  ok: boolean
  enabled: boolean
  ceremonyId: string
  ceremonyName?: string
  ceremonyDate?: string
  imageUrl?: string
  theme?: NamesTheme
  roundLabel?: string
  total: number
  myNumbers: number[]
  names: NameRow[]
  updatedAt?: number
}

const clean = (v: string | null, re: RegExp) => (v && re.test(v.trim()) ? v.trim() : '')

/** อ่าน o/k จาก /names/ หรือ ?view=names (รวม liff.state) · ไม่ใช่หน้านี้ = null */
export function readNamesParams(search: string, pathname = ''): { orderId: string; k: string } | null {
  try {
    const sp = new URLSearchParams(search)
    let src: URLSearchParams | null = null
    if (/^\/names\/?$/i.test(pathname) || sp.get('view') === 'names') src = sp
    else {
      let state = sp.get('liff.state')
      if (state && /%3[fd]/i.test(state)) state = decodeURIComponent(state)
      if (state?.startsWith('?')) state = state.slice(1)
      const st = new URLSearchParams(state || '')
      if (st.get('view') === 'names') src = st
    }
    if (!src) return null
    return {
      orderId: clean(src.get('o') || src.get('orderId'), /^CO[A-Za-z0-9_-]{4,80}$/),
      k: clean(src.get('k'), /^[A-Za-z0-9_-]{16,64}$/),
    }
  } catch {
    return null
  }
}

/** ลิงก์ไปหน้ารายชื่อจากหน้าออร์เดอร์ */
export function buildNamesUrl(orderId: string, k: string, origin = 'https://order.srikanett.com'): string {
  const sp = new URLSearchParams({ o: orderId })
  if (k) sp.set('k', k)
  return `${origin}/names/?${sp.toString()}`
}

/** สถานะที่นับว่า "ร่วมพิธีแล้ว" — ตรงกับ LISTED_STATUSES ฝั่ง CF */
const LISTED = new Set(['ชำระเงินสำเร็จ', 'รอตรวจสอบ', 'รอจัดส่ง', 'สำเร็จ', 'รอประกอบพิธี', 'เสร็จพิธี'])
export function canShowNamesButton(input: { isCeremony: boolean; status?: string; ceremonyId?: string; enabled: boolean }): boolean {
  return !!(input.isCeremony && input.ceremonyId && input.enabled && LISTED.has(String(input.status || '').trim()))
}

/** ธีมของหน้า → (data-ui, data-tone) · auto = ใช้ค่าจาก publicIntegrations/ordercusUi */
export function resolveNamesLook(theme: NamesTheme | undefined, remote: { ui: 'classic' | 'glass' | null; tone: 'royal' | 'smoky' | 'ground' }): { ui: 'classic' | 'glass'; tone: 'royal' | 'smoky' | 'ground' } {
  switch (theme) {
    case 'classic': return { ui: 'classic', tone: 'royal' }
    case 'glass': return { ui: 'glass', tone: 'royal' }
    case 'smoky': return { ui: 'glass', tone: 'smoky' }
    case 'ground': return { ui: 'glass', tone: 'ground' }
    default: return { ui: remote.ui || 'glass', tone: remote.ui === 'classic' ? 'royal' : remote.tone }
  }
}

/** ค้นชื่อ (ไม่สนช่องว่าง/คำนำหน้า "คุณ") */
export function filterNames(rows: NameRow[], q: string): NameRow[] {
  const norm = (s: string) => s.replace(/\s+/g, '').replace(/^คุณ/, '').toLowerCase()
  const n = norm(q || '')
  if (!n) return rows
  return rows.filter((r) => norm(r.name).includes(n))
}
